import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container,
    Row,
    Col,
    Button,
    Card,
    CardHeader,
    CardBody,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    FormGroup,
    Label,
    Input,
    Spinner,
    Pagination,
    PaginationItem,
    PaginationLink,
    ButtonGroup
} from 'reactstrap';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaFilePdf, FaPrint, FaPencilAlt, FaTrash } from 'react-icons/fa';
import { apiURL1 } from '../../config';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import dayjs from 'dayjs';
import moment from 'moment';
import Select from "react-select";

const baseURL = apiURL1;

function FinancialTransactionForm() {
    const token = localStorage.getItem("token");

    const [openModal, setOpenModal] = useState(false);
    const [categories, setCategories] = useState([]);
    const [date, setDate] = useState(new Date());
    const [category, setCategory] = useState('');
    const [amount, setAmount] = useState('');
    const [voucherNumber, setVoucherNumber] = useState('');

    // State variables for filters
    const [filterVoucherNumber, setFilterVoucherNumber] = useState('');
    const [filterMonth, setFilterMonth] = useState(moment().month() + 1);
    const [filterYear, setFilterYear] = useState(moment().year());
    const [filterDate, setFilterDate] = useState('');

    const [transactionType, setTransactionType] = useState('income');
    const [description, setDescription] = useState('');
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [editTransaction, setEditTransaction] = useState(null);
    const [viewTransaction, setViewTransaction] = useState(null);
    const [openViewModal, setOpenViewModal] = useState(false);
    const [deleteTransactionId, setDeleteTransactionId] = useState(null);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [madrasaInfo, setMadrasaInfo] = useState({
        schoolName: '',
        schoolAddress: '',
        schoolPhoneNumber: '',
        schoolEmail: ''
    });
    const categoryOptions = categories.map(cat => ({
        value: cat._id,
        label: cat.name
    }));


    useEffect(() => {
        fetchCategories();
        fetchTransactions();
        fetchMadrasaInfo();
    }, [page, limit, filterMonth, filterYear, filterVoucherNumber, filterDate]);

    const fetchMadrasaInfo = async () => {
        try {
            const response = await axios.get(`${baseURL}/superadmin/school-settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data = response.data;
            if (data && data.length > 0) {
                setMadrasaInfo(data[0]);
            }
        } catch (error) {
            console.error('Error fetching madrasa info:', error);
        }
    };

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${apiURL1}/superadmin/all-expense-categories`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            setCategories(response.data.expenseCategories);
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Error fetching categories');
        } finally {
            setLoading(false);
        }
    };

    const fetchTransactions = async () => {
        try {
            setLoading(true);

            let url = `${apiURL1}/superadmin/monthly-records?page=${page}&limit=${limit}`;

            if (filterVoucherNumber) {
                url += `&voucherNumber=${filterVoucherNumber}`;
            }
            if (filterMonth) {
                url += `&month=${filterMonth}`;
            }
            if (filterYear) {
                url += `&year=${filterYear}`;
            }
            if (filterDate) {
                url += `&date=${filterDate}`;
            }

            const response = await axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });
            setTransactions(response.data.monthlyRecords);
            setTotal(response.data.total);
            setTotalPages(response.data.totalPages);
        } catch (error) {
            console.error('Error fetching transactions:', error);
            toast.error('Error fetching transactions');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = () => {
        setOpenModal(true);
        setEditTransaction(null);
        setDate(new Date());
        setCategory('');
        setAmount('');
        setTransactionType('income');
        setDescription('');
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleSaveTransaction = async () => {
        try {
            setLoading(true);
            const transactionData = {
                date: new Date(date),
                amount: parseFloat(amount),
                description,
                categoryId: category,
                type: transactionType,
            };

            if (editTransaction) {
                await axios.put(
                    `${apiURL1}/superadmin/monthly-records/${editTransaction._id}`,
                    transactionData, {
                    headers: {
                        'Authorization': `Bearer ${token}`, // Include the token in the headers
                    }
                }

                );
                toast.success('Transaction updated successfully');
            } else {
                await axios.post(`${apiURL1}/superadmin/monthly-records`, transactionData, {
                    headers: {
                        'Authorization': `Bearer ${token}`, // Include the token in the headers
                    }
                });
                toast.success('Transaction added successfully');
            }

            fetchTransactions();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving transaction:', error);
            toast.error('Error saving transaction');
        } finally {
            setLoading(false);
        }
    };

    const handleEditTransaction = (transaction) => {
        // console.log('transaction is ', transaction)
        setEditTransaction(transaction);
        setVoucherNumber(transaction.voucherNumber)
        setDate(new Date(transaction.date));
        setCategory(transaction.categoryId?._id);
        setAmount(transaction.amount);
        setTransactionType(transaction.type);
        setDescription(transaction.description);
        setOpenModal(true);
    };

    const handleDeleteTransaction = async () => {
        try {
            setLoading(true);
            await axios.delete(`${apiURL1}/superadmin/monthly-records/${deleteTransactionId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            fetchTransactions();
            handleCloseDeleteModal();
            toast.success('Transaction deleted successfully');
        } catch (error) {
            console.error('Error deleting transaction:', error);
            toast.error('Error deleting transaction');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenViewModal = (transaction) => {
        setViewTransaction(transaction);
        setOpenViewModal(true);
    };

    const handleCloseViewModal = () => {
        setOpenViewModal(false);
    };

    const handleOpenDeleteModal = (id) => {
        setDeleteTransactionId(id);
        setOpenDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setDeleteTransactionId(null);
        setOpenDeleteModal(false);
    };

    const renderPagination = () => {
        const paginationItems = [];
        for (let i = 1; i <= totalPages; i++) {
            paginationItems.push(
                <PaginationItem key={i} active={i === page}>
                    <PaginationLink onClick={() => setPage(i)}>{i}</PaginationLink>
                </PaginationItem>,
            );
        }
        return (
            <Pagination style={{ justifyContent: 'flex-end' }}>
                <PaginationItem disabled={page === 1}>
                    <PaginationLink previous onClick={() => setPage(page - 1)} />
                </PaginationItem>
                {paginationItems}
                <PaginationItem disabled={page === totalPages}>
                    <PaginationLink next onClick={() => setPage(page + 1)} />
                </PaginationItem>
            </Pagination>
        );
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const generateReport = () => {
        const doc = new jsPDF();

        // Add logo and school information
        const img = new Image();
        img.src = "https://res.cloudinary.com/djg7v9dna/image/upload/v1742946371/mi0wavjsehps20kdzaet.png";

        doc.addImage(img, 'PNG', 15, 15, 30, 30);

        doc.setFontSize(14);
        doc.text(madrasaInfo.schoolName, 50, 20); // Adjust position as needed

        doc.setFontSize(10);
        doc.text(madrasaInfo.schoolAddress, 50, 27); // Adjust position as needed
        doc.text(`Phone: ${madrasaInfo.schoolPhoneNumber} | Email: ${madrasaInfo.schoolEmail}`, 50, 34); // Adjust position as needed

        // Define the columns for the table
        const columns = ["Voucher Number", "Date", "Category", "Amount", "Type", "Description"];

        // Prepare the data for the table



        const data = transactions.map(transaction => [
            transaction?.voucherNumber || 'N/A',
            formatDate(transaction.date),
            transaction.categoryId?.name || '-',
            transaction.amount,
            transaction.type,
            transaction.description
        ]);

        // Add the table to the PDF
        doc.autoTable({
            startY: 45,
            head: [columns],
            body: data,
        });

        // Save the PDF
        doc.save('financial_transactions_report.pdf');
    }

    const printReceipt = (transaction) => {
        const voucherNumber = transaction?.voucherNumber || 'N/A';
        const date = dayjs(transaction?.date).format('DD-MM-YYYY') || 'N/A';
        const category = transaction?.categoryId?.name || 'N/A';
        const type = transaction?.type || 'N/A';
        const amount = transaction?.amount || 'N/A';
        const paymentMethod = 'Cash';
        const description = transaction?.description || 'N/A';
        const amountInWordsText = isNaN(amount) ? amount : amountInWords(parseInt(amount));

        const receiptContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Transaction Receipt</title>
    <style>
        @page {
            size: A5; /* Set page size to A5 */
            margin: 0;
        }
        body {
            width: 148mm;
            margin: 0 auto;
            padding: 0;
            box-sizing: border-box;
            font-family: sans-serif;
            background: white;
            font-size: 12px;
        }
        .receipt-container {
            width: 128mm;
            max-height: 190mm;
            padding: 8mm;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            margin: 0 auto;
            page-break-inside: avoid;
            overflow: hidden;
        }
        .receipt-header {
            margin-bottom: 12px;
            text-align: center; /* Ensure header is centered */
            width: 100%; /* Full width to maintain centering */
        }
        .receipt-header h2 {
            margin: 5px 0;
            font-size: 16px;
            text-align: center; /* Explicitly center text */
        }
        .receipt-header h3 {
            margin: 8px 0;
            font-size: 14px;
            text-align: center; /* Explicitly center text */
        }
        .receipt-header p {
            margin: 2px 0;
            font-size: 10px;
            text-align: center; /* Explicitly center text */
        }
        .field-row {
            display: flex;
            justify-content: space-between;
            width: 100%;
            margin-bottom: 8px;
            text-align: left; /* Left align text within rows */
        }
        .field-full {
            text-align: left;
            width: 100%;
            margin-bottom: 8px;
        }
        .field-label {
            margin: 3px 0;
            font-size: 14px;
        }
        .signature-row {
            display: flex;
            justify-content: space-between;
            width: 100%;
            margin-top: 15px;
        }
        .signature-box {
            text-align: center;
        }
        .signature-line {
            border-top: 1px solid black;
            width: 120px;
            margin-top: 18px;
        }
        .signature-row, .signature-box {
            page-break-inside: avoid;
        }
    </style>
</head>
<body>
    <div class="receipt-container">
        <div class="receipt-header">
            <img src="https://res.cloudinary.com/djg7v9dna/image/upload/v1739198257/Logo/kgamz0yjfr9uy8dsbnur.webp" 
                 alt="Madrasa Logo" 
                 style="width: 56px; height: 56px; margin-bottom: 4px; display: block; margin-left: auto; margin-right: auto;">
            <h2>${madrasaInfo.schoolName || 'N/A'}</h2>
            <p>${madrasaInfo.schoolAddress || 'N/A'}</p>
            <p>Phone: ${madrasaInfo.schoolPhoneNumber || 'N/A'} | Email: ${madrasaInfo.schoolEmail || 'N/A'}</p>
            <hr style="border: 0.5px solid #000; width: 100%; margin: 10px 0;">
            <h3>TRANSACTION RECEIPT</h3>
        </div>

        <div style="text-align: right; width: 100%; margin-bottom: 8px;">
            <p style="margin: 3px 0; font-size: 14px;"><strong>Voucher Number:</strong> <span style="border-bottom: 1px solid black;">${voucherNumber}</span></p>
            <p style="margin: 3px 0; font-size: 14px;"><strong>Date:</strong> <span style="border-bottom: 1px solid black;">${date}</span></p>
        </div>

        <div class="field-full">
            <p style="margin: 3px 0; font-size: 14px;"><strong>Payment made for:</strong> <span style="border-bottom: 1px solid black;">${category}</span></p>
        </div>

        <div class="field-row">
            <p style="margin: 3px 0; font-size: 14px;"><strong>Payment Method:</strong> <span style="border-bottom: 1px solid black;">${paymentMethod}</span></p>
            <p style="margin: 3px 0; font-size: 14px;"><strong>Amount:</strong> <span style="border-bottom: 1px solid black;">${amount}</span></p>
        </div>

        <div class="field-full">
            <p style="margin: 3px 0; font-size: 14px;"><strong>Amount in Words:</strong> <span style="border-bottom: 1px solid black;">${amountInWordsText}</span></p>
        </div>

        <div class="field-full" style="margin-top: 10px;">
            <p style="margin: 3px 0; font-size: 14px;"><strong>Description:</strong> <span style="border-bottom: 1px solid black;">${description}</span></p>
        </div>

        <div class="signature-row" style="page-break-inside: avoid;">
            <div class="signature-box">
                <div class="signature-line"></div>
                <p style="margin: 3px 0; font-size: 14px;"><strong>Received By</strong></p>
            </div>
            <div class="signature-box">
                <div class="signature-line"></div>
                <p style="margin: 3px 0; font-size: 14px;"><strong>Checked By</strong></p>
            </div>
        </div>
        <div style="display: flex; justify-content: center; width: 100%; margin-top: 12px; page-break-inside: avoid;">
            <div style="text-align: center; min-width: 140px;">
                <div class="signature-line"></div>
                <p style="margin: 3px 0; font-size: 14px;"><strong>Approved By</strong></p>
            </div>
        </div>
    </div>
    <script>
        window.onload = function() {
            // Set print window to maximum size before printing
            window.moveTo(0, 0);
            window.resizeTo(screen.availWidth, screen.availHeight);
            
            // Short delay to ensure the window is resized before printing
            setTimeout(function() {
                window.print();
                // Close window after printing
                window.onfocus = function() { 
                    setTimeout(function() { window.close(); }, 500);
                }
            }, 200);
        }
    </script>
</body>
</html>
`;

        // Open in a larger window by default
        const printWindow = window.open('', '_blank', `width=${screen.availWidth},height=${screen.availHeight}`);
        printWindow.document.write(receiptContent);
        printWindow.document.close();
    };


    // Helper function to convert numbers to words
    const amountInWords = (num) => {
        // console.log('num is', num);

        const ones = [
            "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"
        ];
        const teens = [
            "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"
        ];
        const tens = [
            "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
        ];

        if (num === 0) return "Zero";
        if (isNaN(num)) return "Invalid Amount";

        let words = "";

        // Handle lakhs (100,000s)
        if (num >= 100000) {
            const lakhsValue = Math.floor(num / 100000);
            words += convertTwoDigits(lakhsValue) + " Lakh ";
            num %= 100000;
        }

        // Handle thousands
        if (num >= 1000) {
            const thousandsValue = Math.floor(num / 1000);
            words += convertTwoDigits(thousandsValue) + " Thousand ";
            num %= 1000;
        }

        // Handle hundreds
        if (num >= 100) {
            words += ones[Math.floor(num / 100)] + " Hundred ";
            num %= 100;
        }

        // Handle remaining tens and ones
        if (num > 0) {
            // Add "and" after hundreds or thousands if there are remaining digits
            if (words.length > 0) {
                words += "and ";
            }
            words += convertTwoDigits(num);
        }

        return words.trim();

        // Helper function to convert two-digit numbers
        function convertTwoDigits(n) {
            if (n === 0) return "";
            if (n < 10) return ones[n];
            if (n >= 10 && n < 20) return teens[n - 10];

            const tensDigit = Math.floor(n / 10);
            const onesDigit = n % 10;

            if (onesDigit === 0) return tens[tensDigit];
            return tens[tensDigit] + " " + ones[onesDigit];
        }
    };

    const handleSearch = () => {
        setPage(1); // Reset to the first page when searching
        fetchTransactions();
    };

    const getDaysInMonth = () => {
        const year = filterYear || moment().year();
        const month = filterMonth || moment().month() + 1;
        return moment(`${year}-${month}`, "YYYY-MM").daysInMonth();
    };

    return (
        <Container>
            <Modal isOpen={openModal} toggle={handleCloseModal}>
                <ModalHeader toggle={handleCloseModal}>
                    {editTransaction ? 'Edit Transaction' : 'Add Transaction'}
                </ModalHeader>
                <ModalBody>
                    <Form>
                        {editTransaction ? (<FormGroup className="mb-4">
                            <Label className="mb-2">Voucher Number</Label>
                            <Input
                                type="text"
                                value={voucherNumber}
                                readOnly
                            />
                        </FormGroup>) : (<></>)}
                        {/* Date Field */}
                        <FormGroup className="mb-4">
                            <Label className="mb-2 mr-3">Date</Label>
                            <DatePicker
                                selected={date ? new Date(date) : null}
                                onChange={(newDate) => setDate(newDate)}
                                className="form-control"
                                dateFormat="dd-MM-yyyy"
                            />
                        </FormGroup>

                        {/* Category Dropdown */}
                        <FormGroup className="mb-4">
                            <Label className="mb-2">Category</Label>
                            <Select
                                options={categoryOptions}
                                value={categoryOptions.find(option => option.value === category)}
                                onChange={(selectedOption) =>
                                    setCategory(selectedOption ? selectedOption.value : "")
                                }
                                placeholder="Select Category"
                                isClearable
                                isSearchable
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isDisabled={Boolean(editTransaction && editTransaction.categoryId?.name === 'Fund Transfer')}
                            />
                        </FormGroup>

                        {/* Amount Field */}
                        <FormGroup className="mb-4">
                            <Label className="mb-2">Amount</Label>
                            <Input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                            />
                        </FormGroup>

                        {/* Transaction Type Radio Buttons */}
                        <FormGroup className="mb-4">
                            <Label className="mb-2">Transaction Type</Label>
                            <div className="d-flex align-items-center">
                                <div className="form-check me-4 mr-5">
                                    <Input
                                        type="radio"
                                        name="transactionType"
                                        value="income"
                                        checked={transactionType === 'income'}
                                        onChange={(e) => setTransactionType(e.target.value)}
                                        className="form-check-input"
                                        disabled={Boolean(editTransaction && editTransaction.categoryId?.name === 'Fund Transfer')}
                                    />
                                    <Label className="form-check-label ms-2">Income</Label>
                                </div>
                                <div className="form-check">
                                    <Input
                                        type="radio"
                                        name="transactionType"
                                        value="expense"
                                        checked={transactionType === 'expense'}
                                        onChange={(e) => setTransactionType(e.target.value)}
                                        className="form-check-input"
                                        disabled={Boolean(editTransaction && editTransaction.categoryId?.name === 'Fund Transfer')}
                                    />
                                    <Label className="form-check-label ms-2">Expense</Label>
                                </div>
                            </div>
                        </FormGroup>

                        {/* Description Field */}
                        <FormGroup className="mb-4">
                            <Label className="mb-2">Description</Label>
                            <Input
                                type="textarea"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </FormGroup>

                        {/* Buttons */}
                        <div className="d-flex justify-content-end">
                            <Button color="primary" onClick={handleSaveTransaction} disabled={loading}>
                                {loading ? <Spinner size="sm" /> : 'Save'}
                            </Button>
                            <Button color="secondary" onClick={handleCloseModal} className="ms-2">
                                Cancel
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>

            <Row className="mb-3">
                <Col>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button color="primary" size="sm" onClick={handleOpenModal}>
                            Add Transaction
                        </Button>
                        <Button color="primary" size="sm" onClick={generateReport} disabled={loading}>
                            {loading ? <Spinner size="sm" /> : <FaFilePdf />} Generate Report
                        </Button>
                    </div>
                </Col>
            </Row>

            {/* Filter Inputs */}
            <Row className="mb-3">
                <Col md="3">
                    <FormGroup>
                        <Label>Voucher Number</Label>
                        <Input
                            type="text"
                            value={filterVoucherNumber}
                            onChange={(e) => setFilterVoucherNumber(e.target.value)}
                        />
                    </FormGroup>
                </Col>
                <Col md="3">
                    <FormGroup>
                        <Label>Month</Label>
                        <Input
                            type="select"
                            value={filterMonth}
                            onChange={(e) => setFilterMonth(e.target.value)}
                        >
                            <option value="">Select Month</option>
                            {[...Array(12)].map((_, i) => {
                                const month = i + 1;
                                const monthName = new Date(2000, i, 1).toLocaleString('default', { month: 'long' });
                                return (
                                    <option key={month} value={month}>{monthName}</option>
                                );
                            })}
                        </Input>
                    </FormGroup>
                </Col>
                <Col md="3">
                    <FormGroup>
                        <Label>Year</Label>
                        <Input
                            type="number"
                            value={filterYear}
                            onChange={(e) => setFilterYear(e.target.value)}
                        />
                    </FormGroup>
                </Col>
                <Col md="3">
                    <FormGroup>
                        <Label>Date</Label>
                        <Input
                            type="select"
                            value={filterDate}
                            onChange={(e) => setFilterDate(e.target.value)}
                        >
                            <option value="">Select Date</option>
                            {[...Array(getDaysInMonth())].map((_, i) => {
                                const day = i + 1;
                                return (
                                    <option key={day} value={day}>{day}</option>
                                );
                            })}
                        </Input>
                    </FormGroup>
                </Col>
                <Col md="3" style={{ display: 'flex', alignItems: 'flex-end' }}>
                    {/* <Button color="primary" onClick={handleSearch}>
                        Search
                    </Button> */}
                </Col>
            </Row>

            <Card>
                <CardHeader>
                    <h3>Financial Transaction</h3>
                </CardHeader>
                <CardBody>
                    <div className="table-responsive">
                        <Table>
                            <thead>
                                <tr>
                                    <th style={{ maxWidth: '120px', wordBreak: 'break-word' }}>Voucher Number</th>
                                    <th style={{ maxWidth: '120px', wordBreak: 'break-word' }}>Date</th>
                                    <th style={{ maxWidth: '150px', wordBreak: 'break-word' }}>Category</th>
                                    <th style={{ maxWidth: '100px', wordBreak: 'break-word' }}>Amount</th>
                                    <th style={{ maxWidth: '100px', wordBreak: 'break-word' }}>Type</th>
                                    <th style={{ maxWidth: '250px', wordBreak: 'break-word' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions && transactions.length > 0 ? (
                                    transactions.map((transaction) => (
                                        <tr key={transaction._id}>
                                            <td style={{ maxWidth: '120px', wordBreak: 'break-word' }}>{transaction?.voucherNumber || 'N/A'}</td>
                                            <td style={{ maxWidth: '120px', wordBreak: 'break-word' }}>
                                                {(() => {
                                                    const d = new Date(transaction.date);
                                                    const day = String(d.getDate()).padStart(2, '0');
                                                    const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
                                                    const year = d.getFullYear();
                                                    return `${day}-${month}-${year}`;
                                                })()}
                                            </td>
                                            <td style={{ maxWidth: '150px', wordBreak: 'break-word' }}>{transaction.categoryId?.name || '-'}</td>
                                            <td style={{ maxWidth: '100px', wordBreak: 'break-word' }}>{transaction.amount}</td>
                                            <td style={{ maxWidth: '100px', wordBreak: 'break-word' }}>{transaction.type}</td>
                                            <td style={{ maxWidth: '250px', wordBreak: 'break-word' }}>
                                                <ButtonGroup size="sm">
                                                    <Button color="info" className="mr-1" onClick={() => handleOpenViewModal(transaction)}>
                                                        View
                                                    </Button>
                                                    <Button color="primary" className="mr-1" onClick={() => handleEditTransaction(transaction)}>
                                                        <FaPencilAlt />
                                                    </Button>
                                                    <Button color="danger" className="mr-1" onClick={() => handleOpenDeleteModal(transaction._id)}>
                                                        <FaTrash />
                                                    </Button>
                                                    <Button color="secondary" onClick={() => printReceipt(transaction)}>
                                                        <FaPrint />
                                                    </Button>
                                                </ButtonGroup>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5">No transactions found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                    <Row>
                        <Col style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {renderPagination()}
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            <Modal isOpen={openViewModal} toggle={handleCloseViewModal}>
                <ModalHeader toggle={handleCloseViewModal}>Transaction Details</ModalHeader>
                <ModalBody>
                    {viewTransaction && (
                        <div>
                            <p>
                                <b>Voucher Number:</b> {viewTransaction?.voucherNumber || 'N.A'}
                            </p>
                            <p>
                                <b>Date:</b> {formatDate(viewTransaction.date)}
                            </p>
                            <p>
                                <b>Category:</b> {viewTransaction.categoryId?.name || '-'}
                            </p>
                            <p>
                                <b>Amount:</b> {viewTransaction.amount}
                            </p>
                            <p>
                                <b>Type:</b> {viewTransaction.type}
                            </p>
                            <p>
                                <b>Description:</b> {viewTransaction.description}
                            </p>
                        </div>
                    )}
                </ModalBody>
            </Modal>

            <Modal isOpen={openDeleteModal} toggle={handleCloseDeleteModal}>
                <ModalHeader toggle={handleCloseDeleteModal}>Delete Transaction</ModalHeader>
                <ModalBody>
                    <p>Are you sure you want to delete this transaction?</p>
                    <Button color="danger" onClick={handleDeleteTransaction} disabled={loading}>
                        {loading ? <Spinner size="sm" /> : 'Delete'}
                    </Button>
                    <Button color="secondary" onClick={handleCloseDeleteModal} className="ms-2">
                        Cancel
                    </Button>
                </ModalBody>
            </Modal>
        </Container>
    );
}

export default FinancialTransactionForm;
